import { NO_SHARED_WORKER_MESSAGE } from "@/features/workers/worker-access";

export type TaskCreationBlockerReason =
  | "repositories"
  | "worker-check"
  | "worker-access"
  | "workers"
  | "providers";

export interface TaskCreationBlocker {
  reason: TaskCreationBlockerReason;
  title: string;
  message: string;
}

export function taskCreationBlocker({
  repositoryCount,
  checkingWorkers,
  workerCheckFailed,
  registeredWorkerCount,
  usableWorkerCount,
  onlineWorkerCount,
  readyProviderCount,
}: {
  repositoryCount: number;
  checkingWorkers: boolean;
  workerCheckFailed: boolean;
  /** Every worker in the organization, whoever owns it. */
  registeredWorkerCount: number;
  /** The ones this member may run work on (`WorkerInfo.canUse`). */
  usableWorkerCount: number;
  /** Usable, online, and able to reach the chosen repository. */
  onlineWorkerCount: number;
  readyProviderCount: number;
}): TaskCreationBlocker | undefined {
  if (repositoryCount === 0) {
    return {
      reason: "repositories",
      title: "No repositories available",
      message: "Add a repository before creating a task.",
    };
  }

  // A pending availability check is not itself an error. The form remains
  // disabled until the worker response selects a ready provider.
  if (checkingWorkers) return undefined;

  if (workerCheckFailed) {
    return {
      reason: "worker-check",
      title: "Couldn’t check worker availability",
      message: "Check your connection and try again before creating a task.",
    };
  }

  // Machines exist but none is shared with this member: "connect a worker"
  // would be the wrong advice, because the fix is a teammate's grant or a
  // machine of their own.
  if (registeredWorkerCount > 0 && usableWorkerCount === 0) {
    return {
      reason: "worker-access",
      title: "No worker shared with you",
      message: NO_SHARED_WORKER_MESSAGE,
    };
  }

  if (onlineWorkerCount === 0) {
    return {
      reason: "workers",
      title: "No workers connected",
      message: "Connect a worker from the Workers page before creating a task.",
    };
  }

  if (readyProviderCount === 0) {
    return {
      reason: "providers",
      title: "No agents ready",
      message: "Install and authenticate Claude Code or Codex on a connected worker.",
    };
  }

  return undefined;
}
